import { storageKey } from './storage-key.js';

const AFF_CODE_PARAM = 'aff_code';
const AFF_CODE_MAX_LENGTH = 32;
export const AFF_CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface StoredAffCode {
  code: string;
  expiresAt: number;
}

/** The `?aff_code=` of a URL search string, trimmed and uppercased; null when absent or over 32 chars. */
export function affCodeFromSearch(search: string): string | null {
  const code = new URLSearchParams(search).get(AFF_CODE_PARAM)?.trim();
  if (!code || code.length > AFF_CODE_MAX_LENGTH) return null;
  return code.toUpperCase();
}

/**
 * Keep an affiliate code for 30 days; a newer link replaces an older one.
 *
 * The key is per tenant (`storageKey`), so call it once /site-config has
 * settled: written while the tenant is still unresolved it lands in the
 * fallback bucket, and the register page, which runs with the tenant known,
 * would never find it.
 */
export function rememberAffCode(code: string, now: number = Date.now()): void {
  const stored: StoredAffCode = { code, expiresAt: now + AFF_CODE_TTL_MS };
  try {
    localStorage.setItem(storageKey(AFF_CODE_PARAM), JSON.stringify(stored));
  } catch {
    // storage blocked (private mode, quota): the link simply is not remembered
  }
}

/** The remembered affiliate code, or undefined when none was stored or it has expired. */
export function readAffCode(now: number = Date.now()): string | undefined {
  try {
    const raw = localStorage.getItem(storageKey(AFF_CODE_PARAM));
    if (!raw) return undefined;
    const stored = JSON.parse(raw) as Partial<StoredAffCode> | null;
    if (typeof stored?.code === 'string' && typeof stored.expiresAt === 'number' && stored.expiresAt > now) {
      return stored.code;
    }
    localStorage.removeItem(storageKey(AFF_CODE_PARAM));
    return undefined;
  } catch {
    return undefined;
  }
}
